import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import {
  isDigestResponse,
  isProgressResponse,
  isRadarResponse,
  isRiskResponse,
  isNarrativeResponse,
  isRAGResponse,
  isEmailDraftResponse,
} from './types';
import AccountRadarView from './AccountRadarView';
import RiskAssessmentView from './RiskAssessmentView';
import AccountNarrativeView from './AccountNarrativeView';
import WeeklyDigestView from './WeeklyDigestView';
import ProgressSummaryView from './ProgressSummaryView';

interface Props {
  payload: any;
  compact?: boolean;
  /** When true, render an "Open full view" deep link that carries the payload as nav state. */
  showOpenFull?: boolean;
}

/**
 * Resolve the destination route for a result payload. Critically, digest/progress
 * do NOT have their own routes — they render inside /insights/query — so we never
 * link to /insights/digest or /insights/progress (which would hit the catch-all
 * route and bounce to the landing page).
 */
function resolveDeepLink(payload: any): string | null {
  if (isRadarResponse(payload)) return '/insights/radar';
  if (
    isRiskResponse(payload) ||
    isNarrativeResponse(payload) ||
    isDigestResponse(payload) ||
    isProgressResponse(payload) ||
    isRAGResponse(payload)
  ) {
    return '/insights/query';
  }
  return null;
}

const InsightResultCard: React.FC<Props> = ({ payload, compact = false, showOpenFull = false }) => {
  const navigate = useNavigate();
  if (!payload) return null;

  let content: React.ReactNode = null;

  if (isRadarResponse(payload)) {
    content = <AccountRadarView data={payload} compact={compact} />;
  } else if (isRiskResponse(payload)) {
    content = <RiskAssessmentView data={payload} compact={compact} />;
  } else if (isNarrativeResponse(payload)) {
    content = <AccountNarrativeView data={payload} compact={compact} />;
  } else if (isDigestResponse(payload)) {
    content = <WeeklyDigestView data={payload} compact={compact} />;
  } else if (isProgressResponse(payload)) {
    content = <ProgressSummaryView data={payload} compact={compact} />;
  } else if (isRAGResponse(payload)) {
    content = (
      <div className="prose dark:prose-invert max-w-none text-sm">
        <p className="whitespace-pre-wrap">{payload.response?.analysis}</p>
        {payload.response?.key_insights?.length > 0 && (
          <ul className="mt-2 list-disc list-inside">
            {payload.response.key_insights.slice(0, compact ? 3 : 10).map((insight: string, i: number) => (
              <li key={i}>{insight}</li>
            ))}
          </ul>
        )}
      </div>
    );
  } else if (isEmailDraftResponse(payload)) {
    content = (
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2 text-muted-foreground">
          <span className="font-medium text-foreground">{payload.customer_name}</span>
          <span>·</span>
          <span className="capitalize">{(payload.email_type || 'update').replace(/_/g, ' ')}</span>
        </div>
        <p className="font-medium text-foreground">{payload.subject}</p>
        <p className="line-clamp-3 whitespace-pre-wrap text-muted-foreground">{payload.body}</p>
      </div>
    );
  } else {
    content = <p className="text-sm text-muted-foreground">{payload.content || 'Result ready.'}</p>;
  }

  const deepLink = showOpenFull ? resolveDeepLink(payload) : null;

  const handleOpenFull = () => {
    if (!deepLink) return;
    navigate(deepLink, {
      state: {
        mcpResponse: payload,
        submittedQuery: payload.query || payload.topic || payload.customer_name || '',
      },
    });
  };

  return (
    <div className="space-y-3">
      {content}
      {deepLink && (
        <button
          type="button"
          onClick={handleOpenFull}
          className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1"
        >
          Open full view
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

export default InsightResultCard;
